/**
 * ModelSelector Component
 * Dynamic model selection with provider grouping (tab-style)
 */

import { useMemo, useState, useEffect } from 'react';
import { ChevronDown, Check, Sparkles, Layers, Blend } from 'lucide-react';
import type { ImageModelInfo } from '@/types/playground';
import { VENDOR_INFO } from '@/types/playground';
import { useI18n } from '@/i18n';

interface ModelSelectorProps {
  models: ImageModelInfo[];
  selectedModel: string;
  onModelChange: (modelId: string) => void;
  operationMode?: 'generate' | 'edit' | 'fuse';
  isLoading?: boolean;
  compact?: boolean;
}

interface ModelGroup {
  provider: string;
  label: string;
  description: string;
  models: ImageModelInfo[];
}

export function ModelSelector({
  models,
  selectedModel,
  onModelChange,
  operationMode = 'generate',
  isLoading = false,
  compact = false,
}: ModelSelectorProps) {
  const { t } = useI18n();
  // Selected provider for tab view
  const [selectedProvider, setSelectedProvider] = useState<string>('');

  // Group models by provider
  const modelGroups = useMemo((): ModelGroup[] => {
    const groups = new Map<string, ImageModelInfo[]>();

    // Filter models by operation mode
    const filteredModels = models.filter((m) => {
      switch (operationMode) {
        case 'generate':
          return m.supports_generate;
        case 'edit':
          return m.supports_edit;
        case 'fuse':
          return m.supports_fuse;
        default:
          return true;
      }
    });

    // Group by provider
    filteredModels.forEach((model) => {
      const provider = model.vendor;
      if (!groups.has(provider)) {
        groups.set(provider, []);
      }
      groups.get(provider)!.push(model);
    });

    // Convert to array with provider info
    return Array.from(groups.entries()).map(([provider, providerModels]) => ({
      provider,
      label: VENDOR_INFO[provider]?.label || provider,
      description: VENDOR_INFO[provider]?.description || '',
      models: providerModels,
    }));
  }, [models, operationMode]);

  // Auto-select first provider on initial load only
  useEffect(() => {
    if (modelGroups.length > 0 && !selectedProvider) {
      // Find provider of currently selected model
      const selectedModelInfo = models.find((m) => m.model_id === selectedModel);
      if (selectedModelInfo) {
        const provider = selectedModelInfo.vendor;
        if (modelGroups.some((g) => g.provider === provider)) {
          setSelectedProvider(provider);
          return;
        }
      }
      // Fallback to first provider
      setSelectedProvider(modelGroups[0].provider);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelGroups.length]);

  // Get selected model info
  const selectedModelInfo = useMemo(
    () => models.find((m) => m.model_id === selectedModel),
    [models, selectedModel]
  );

  // Get current provider's models
  const currentProviderModels = useMemo(
    () => modelGroups.find((g) => g.provider === selectedProvider)?.models || [],
    [modelGroups, selectedProvider]
  );

  // Get capability icons for a model
  const getCapabilityIcons = (model: ImageModelInfo) => {
    const icons = [];
    if (model.supports_generate) icons.push({ icon: Sparkles, title: t('playground.labels.capabilityGenerate') });
    if (model.supports_edit) icons.push({ icon: Layers, title: t('playground.labels.capabilityEdit') });
    if (model.supports_fuse) icons.push({ icon: Blend, title: t('playground.labels.capabilityFuse') });
    return icons;
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  if (compact) {
    // Compact dropdown for inline use
    return (
      <div className="relative">
        <select
          value={selectedModel}
          onChange={(e) => onModelChange(e.target.value)}
          className="w-full px-3 py-2 pr-8 text-sm border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
        >
          {modelGroups.map((group) => (
            <optgroup key={group.provider} label={group.label}>
              {group.models.map((model) => (
                <option key={model.model_id} value={model.model_id}>
                  {model.display_name}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    );
  }

  // Full card-based selector with tab-style provider switching
  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-700">{t('playground.labels.model')}</label>

      {/* Provider tabs */}
      <div className="flex flex-wrap gap-2">
        {modelGroups.map((group) => {
          const isActive = selectedProvider === group.provider;
          // Check if any model in this group is selected
          const hasSelectedModel = group.models.some((m) => m.model_id === selectedModel);

          return (
            <button
              key={group.provider}
              type="button"
              onClick={() => setSelectedProvider(group.provider)}
              className={`py-1.5 px-4 rounded-lg border-2 text-sm font-medium transition-all cursor-pointer ${
                isActive
                  ? 'bg-primary-100 border-primary-500 text-primary-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {group.label}
              {hasSelectedModel && !isActive && (
                <Check className="w-3 h-3 inline ml-1 text-primary-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Model cards for selected provider */}
      <div className="grid grid-cols-2 gap-2">
        {currentProviderModels.map((model) => {
          const isSelected = selectedModel === model.model_id;
          const capabilities = getCapabilityIcons(model);

          return (
            <button
              key={model.model_id}
              type="button"
              onClick={() => onModelChange(model.model_id)}
              className={`relative p-3 text-left rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute top-2 right-2">
                  <Check className="w-4 h-4 text-primary-600" />
                </div>
              )}

              {/* Model name */}
              <div className="font-medium text-sm text-gray-900 pr-6">
                {model.display_name}
              </div>

              {/* Capability icons */}
              <div className="flex items-center gap-1 mt-1">
                {capabilities.map(({ icon: Icon, title }, idx) => (
                  <div
                    key={idx}
                    className="w-4 h-4 text-gray-400"
                    title={title}
                  >
                    <Icon className="w-full h-full" />
                  </div>
                ))}
              </div>

              {/* Quick info */}
              <div className="mt-2 text-xs text-gray-500">
                {model.sizes.length > 0 && (
                  <span>{model.sizes.slice(0, 3).join(', ')}</span>
                )}
                {model.max_images_fuse > 0 && operationMode === 'fuse' && (
                  <span className="ml-2">{t('playground.labels.maxImagesForFuse').replace('{count}', String(model.max_images_fuse))}</span>
                )}
              </div>

              {/* Price */}
              {model.cost_per_image > 0 && (
                <div className="mt-1 text-xs text-amber-600 font-medium">
                  ¥{model.cost_per_image.toFixed(2)}/张
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected model details */}
      {selectedModelInfo && (
        <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <strong>{t('playground.labels.resolution')}:</strong> {selectedModelInfo.sizes.join(', ')}
            </span>
            {selectedModelInfo.quality_options.length > 0 && (
              <span>
                <strong>{t('playground.labels.quality')}:</strong> {selectedModelInfo.quality_options.join(', ')}
              </span>
            )}
            {selectedModelInfo.supports_web_search && (
              <span className="text-primary-600">{t('playground.labels.supportsWebSearch')}</span>
            )}
            {selectedModelInfo.supports_transparency && (
              <span className="text-primary-600">{t('playground.labels.supportsTransparency')}</span>
            )}
            {selectedModelInfo.cost_per_image > 0 && (
              <span className="text-amber-600">
                <strong>{t('playground.labels.pricePerImage')}:</strong> ¥{selectedModelInfo.cost_per_image.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
